import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api, formatApiError } from "../lib/api";
import { ShoppingBasket, Trash2, Plus, Truck, FileText, X, Loader2, ListPlus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const COMMON_UNITS = ["pz", "kg", "g", "l", "ml"];

export function ShoppingListDialog({ open, onOpenChange, onChanged, onPrintReady }) {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [draft, setDraft] = useState({}); // { name -> {quantity, unit, unit_price, supplier_override} }
    const [suppliers, setSuppliers] = useState([]);
    const [mode, setMode] = useState("auto"); // auto | manual
    const [supplierMode, setSupplierMode] = useState("existing"); // existing | new
    const [supplierId, setSupplierId] = useState("");
    const [supplierPrices, setSupplierPrices] = useState({}); // {name_lower: {price, unit}} - per fornitore singolo manuale
    const [allSupplierPrices, setAllSupplierPrices] = useState({}); // {name_lower: [{supplier_id, supplier_name, price, unit}]}
    const [newSup, setNewSup] = useState({ name: "", contact: "", email: "" });
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();

    const reload = async () => {
        setLoading(true);
        try {
            const [{ data: sl }, { data: sup }] = await Promise.all([
                api.get("/shopping-list"),
                api.get("/suppliers"),
            ]);
            setList(sl.items || []);
            setSuppliers(sup);
            if (sup.length > 0 && !supplierId) setSupplierId(sup[0].id);
            setSupplierMode(sup.length > 0 ? "existing" : "new");
            // Fetch in parallel i listini di TUTTI i fornitori per la mappa "DA LISTINO" e gli override
            const all = {};
            await Promise.all(
                sup.map(async (s) => {
                    try {
                        const { data } = await api.get(`/shopping-list/supplier-prices/${s.id}`);
                        for (const [nameLower, info] of Object.entries(data || {})) {
                            if (!all[nameLower]) all[nameLower] = [];
                            all[nameLower].push({
                                supplier_id: s.id,
                                supplier_name: s.name,
                                price: info.price,
                                unit: info.unit,
                            });
                        }
                    } catch {
                        // skip per fornitore non accessibile
                    }
                })
            );
            setAllSupplierPrices(all);

            // hydrate draft preserving previous values
            setDraft((prev) => {
                const next = {};
                for (const it of sl.items || []) {
                    next[it.name] = prev[it.name] || {
                        quantity: it.quantity ?? 1,
                        unit: it.unit ?? "pz",
                        unit_price: "",
                        supplier_override: "auto",
                    };
                }
                return next;
            });
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    // Quando cambia il fornitore esistente, scarica i suoi prezzi e auto-compila
    useEffect(() => {
        if (!open) return;
        if (supplierMode !== "existing" || !supplierId) {
            setSupplierPrices({});
            return;
        }
        let cancelled = false;
        api.get(`/shopping-list/supplier-prices/${supplierId}`)
            .then(({ data }) => {
                if (cancelled) return;
                setSupplierPrices(data || {});
                // Auto-compila i prezzi vuoti per gli articoli che corrispondono
                setDraft((prev) => {
                    const next = { ...prev };
                    let filled = 0;
                    for (const it of list) {
                        const sp = data[it.name.toLowerCase()];
                        const cur = next[it.name] || { quantity: 1, unit: "pz", unit_price: "" };
                        if (sp && (cur.unit_price === "" || cur.unit_price == null)) {
                            next[it.name] = {
                                ...cur,
                                unit_price: sp.price,
                                unit: sp.unit || cur.unit,
                            };
                            filled += 1;
                        }
                    }
                    if (filled > 0) {
                        toast.success(`${filled} prezz${filled === 1 ? "o" : "i"} auto-compilati dal listino`);
                    }
                    return next;
                });
            })
            .catch(() => setSupplierPrices({}));
        return () => { cancelled = true; };
    }, [supplierId, supplierMode, open, list.length]);

    useEffect(() => {
        if (open) reload();
    }, [open]);

    const updateDraft = (name, field, value) => {
        setDraft((d) => {
            const cur = d[name] || { quantity: 1, unit: "pz", unit_price: "", supplier_override: "auto" };
            const next = { ...d, [name]: { ...cur, [field]: value } };
            // Se l'utente cambia il fornitore manuale e quel fornitore ha un
            // prezzo a listino, auto-fill prezzo+unità (solo se non già editati)
            if (field === "supplier_override" && value !== "auto" && value !== "generic") {
                const candidates = allSupplierPrices[name.toLowerCase()] || [];
                const c = candidates.find((cd) => cd.supplier_id === value);
                if (c) {
                    next[name].unit_price = c.price;
                    if (c.unit) next[name].unit = c.unit;
                }
            }
            return next;
        });
    };

    const removeItem = async (name) => {
        try {
            await api.delete(`/shopping-list/item?name=${encodeURIComponent(name)}`);
            await reload();
            onChanged?.();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const clearAll = async () => {
        if (!window.confirm("Svuotare la lista mancanti?")) return;
        try {
            await api.delete("/shopping-list");
            await reload();
            onChanged?.();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const canCreateOrder = useMemo(() => {
        if (list.length === 0) return false;
        if (mode === "auto") return true; // backend gestisce match + generico
        if (supplierMode === "existing") return !!supplierId;
        return newSup.name.trim().length > 0;
    }, [list.length, mode, supplierMode, supplierId, newSup.name]);

    const createAutoSplitOrder = async () => {
        setBusy(true);
        try {
            const items = list.map((it) => {
                const d = draft[it.name] || {};
                const obj = {
                    name: it.name,
                    quantity: parseFloat(d.quantity) || 1,
                };
                if (d.unit) obj.unit = d.unit;
                const p = parseFloat(d.unit_price);
                if (Number.isFinite(p)) obj.unit_price = p;
                if (d.supplier_override && d.supplier_override !== "auto") {
                    obj.supplier_override = d.supplier_override;
                }
                return obj;
            });
            const { data } = await api.post("/shopping-list/auto-split", {
                items,
                clear_after: true,
            });
            const sup = data.supplier_count;
            const gen = data.generic_items;
            const parts = [];
            if (sup > 0) parts.push(`${sup} ordin${sup === 1 ? "e" : "i"} fornitore`);
            if (gen > 0) parts.push(`${gen} articol${gen === 1 ? "o" : "i"} senza fornitore`);
            toast.success(`Pronto: ${parts.join(" + ") || "nessun ordine"}`);
            onChanged?.();
            onPrintReady?.(data);
            onOpenChange(false);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const createManualOrder = async () => {
        if (!canCreateOrder) return;
        setBusy(true);
        try {
            const items = list.map((it) => {
                const d = draft[it.name] || {};
                const obj = {
                    name: it.name,
                    quantity: parseFloat(d.quantity) || 1,
                    unit: d.unit || "pz",
                };
                const p = parseFloat(d.unit_price);
                if (Number.isFinite(p)) obj.unit_price = p;
                return obj;
            });
            const payload = { items, clear_after: true };
            if (supplierMode === "existing") payload.supplier_id = supplierId;
            else {
                payload.new_supplier_name = newSup.name.trim();
                payload.new_supplier_contact = newSup.contact;
                payload.new_supplier_email = newSup.email;
            }
            const { data } = await api.post("/shopping-list/to-order", payload);
            if (data.supplier_created) toast.success(`Fornitore "${newSup.name}" creato`);
            const total = (data.groups?.[0]?.total || 0).toFixed(2);
            toast.success(`Ordine pronto: ${items.length} articoli · totale ${total}€`);
            onChanged?.();
            onPrintReady?.(data);
            onOpenChange(false);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-3xl p-0 overflow-hidden bg-paper flex flex-col max-h-[90vh]"
                data-testid="shopping-list-dialog"
            >
                <DialogHeader className="px-6 pt-6 pb-4 border-b border-brand-line bg-paper">
                    <div className="flex items-start gap-3">
                        <div className="h-11 w-11 rounded-full bg-brand-warning/15 text-brand-warning flex items-center justify-center shrink-0">
                            <ShoppingBasket size={20} />
                        </div>
                        <div className="flex-1">
                            <div className="eyebrow text-brand-warning mb-1">Lista mancanti</div>
                            <DialogTitle className="font-display text-2xl text-brand-ink">
                                {list.length === 0
                                    ? "La lista è vuota"
                                    : `${list.length} ingredient${list.length === 1 ? "e" : "i"} da ordinare`}
                            </DialogTitle>
                            <p className="text-sm text-brand-muted mt-1">
                                Imposta quantità e unità, scegli un fornitore esistente o creane uno nuovo,
                                poi genera il documento d'ordine pronto da stampare.
                            </p>
                        </div>
                        {list.length > 0 && (
                            <button
                                onClick={clearAll}
                                data-testid="shopping-clear-btn"
                                className="text-brand-muted hover:text-brand-danger p-2 shrink-0"
                                title="Svuota lista"
                            >
                                <Trash2 size={16} />
                            </button>
                        )}
                    </div>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto p-6 space-y-5">
                    {loading && (
                        <div className="flex items-center justify-center py-8 text-brand-muted">
                            <Loader2 size={18} className="animate-spin" />
                        </div>
                    )}
                    {!loading && list.length === 0 && (
                        <div className="rounded-xl border border-dashed border-brand-line p-10 text-center">
                            <ListPlus size={32} className="mx-auto text-brand-muted mb-2" />
                            <div className="font-display text-lg text-brand-ink">Nessun ingrediente in lista</div>
                            <p className="text-sm text-brand-muted mt-1">
                                Vai al catalogo ricette e usa "Metti gli ingredienti mancanti in lista"
                                sui piatti che hai aggiunto al menu.
                            </p>
                            <button
                                onClick={() => { onOpenChange(false); navigate("/recipes"); }}
                                className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-navy text-white px-5 py-2 text-sm font-semibold hover:bg-brand-navyLight transition"
                            >
                                Vai al catalogo
                            </button>
                        </div>
                    )}

                    {!loading && list.length > 0 && (
                        <>
                            <div className="space-y-2" data-testid="shopping-items">
                                {list.map((it) => {
                                    const d = draft[it.name] || { quantity: 1, unit: "pz", unit_price: "", supplier_override: "auto" };
                                    const candidates = allSupplierPrices[it.name.toLowerCase()] || [];
                                    const hasMatch = candidates.length > 0;
                                    const bestCandidate = hasMatch
                                        ? candidates.reduce((a, b) => (a.price <= b.price ? a : b))
                                        : null;
                                    const override = d.supplier_override || "auto";
                                    // Risolvi quale fornitore verrà effettivamente usato
                                    let resolvedSup = null;
                                    let resolvedFromList = false;
                                    if (override === "generic") {
                                        resolvedSup = null;
                                    } else if (override === "auto") {
                                        if (mode === "auto") {
                                            resolvedSup = bestCandidate;
                                            resolvedFromList = !!bestCandidate;
                                        } else {
                                            // modalità manuale: fornitore selezionato in basso
                                            const sel = suppliers.find((s) => s.id === supplierId);
                                            if (sel) {
                                                resolvedSup = { supplier_id: sel.id, supplier_name: sel.name };
                                                const c = candidates.find((c) => c.supplier_id === sel.id);
                                                resolvedFromList = !!c;
                                            }
                                        }
                                    } else {
                                        // override su fornitore specifico
                                        const sel = suppliers.find((s) => s.id === override);
                                        if (sel) {
                                            resolvedSup = { supplier_id: sel.id, supplier_name: sel.name };
                                            const c = candidates.find((c) => c.supplier_id === sel.id);
                                            resolvedFromList = !!c;
                                        }
                                    }
                                    const qty = parseFloat(d.quantity) || 0;
                                    const price = parseFloat(d.unit_price) || 0;
                                    const lineTotal = qty * price;
                                    return (
                                        <div
                                            key={it.name}
                                            className={`bg-white rounded-xl border p-3 ${resolvedSup === null ? "border-brand-warning/40" : "border-brand-line"}`}
                                            data-testid={`shopping-item-${it.name}`}
                                        >
                                            <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                                                <span className={`h-8 w-1.5 rounded-full shrink-0 ${resolvedSup === null ? "bg-brand-warning" : "bg-brand-success"}`} />
                                                <div className="flex-1 min-w-[140px]">
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                        <div className="font-semibold text-sm text-brand-ink truncate">
                                                            {it.name}
                                                        </div>
                                                        {resolvedFromList && (
                                                            <span
                                                                className="inline-flex items-center rounded-full bg-brand-success/12 text-brand-success border border-brand-success/30 px-1.5 py-0 text-[8px] uppercase tracking-wider font-bold"
                                                                title="Prezzo presente nel listino del fornitore"
                                                            >
                                                                DA LISTINO
                                                            </span>
                                                        )}
                                                        {resolvedSup === null && (
                                                            <span className="inline-flex items-center rounded-full bg-brand-warning/15 text-brand-warning border border-brand-warning/30 px-1.5 py-0 text-[8px] uppercase tracking-wider font-bold">
                                                                Senza fornitore
                                                            </span>
                                                        )}
                                                    </div>
                                                    {it.recipe_names?.length > 0 && (
                                                        <div className="text-[10px] uppercase tracking-wider text-brand-muted truncate">
                                                            Per {it.recipe_names.slice(0, 3).join(", ")}
                                                            {it.recipe_names.length > 3 && ` +${it.recipe_names.length - 3}`}
                                                        </div>
                                                    )}
                                                </div>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={d.quantity}
                                                    onChange={(e) => updateDraft(it.name, "quantity", e.target.value)}
                                                    data-testid={`shopping-qty-${it.name}`}
                                                    placeholder="Qtà"
                                                    className="w-16 px-2 py-1.5 text-right rounded-lg border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                                                />
                                                <select
                                                    value={d.unit}
                                                    onChange={(e) => updateDraft(it.name, "unit", e.target.value)}
                                                    data-testid={`shopping-unit-${it.name}`}
                                                    className="w-14 px-1 py-1.5 rounded-lg border border-brand-line text-sm bg-white focus:outline-none focus:border-brand-gold"
                                                >
                                                    {COMMON_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                                                </select>
                                                <div className="flex items-center w-24 relative">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={d.unit_price}
                                                        onChange={(e) => updateDraft(it.name, "unit_price", e.target.value)}
                                                        placeholder="€"
                                                        data-testid={`shopping-price-${it.name}`}
                                                        className={`w-full pl-2 pr-5 py-1.5 text-right rounded-lg border text-sm focus:outline-none ${
                                                            resolvedFromList ? "border-brand-success/40 bg-brand-success/5 focus:border-brand-success" : "border-brand-line focus:border-brand-gold"
                                                        }`}
                                                    />
                                                    <span className="absolute right-1.5 text-[10px] text-brand-muted pointer-events-none">€</span>
                                                </div>
                                                <div className="w-20 text-right shrink-0 hidden sm:block">
                                                    <div className="text-[10px] uppercase tracking-wider text-brand-muted">Totale</div>
                                                    <div className="font-semibold text-sm text-brand-ink">
                                                        {lineTotal.toFixed(2)}€
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => removeItem(it.name)}
                                                    className="text-brand-muted hover:text-brand-danger p-1.5"
                                                    data-testid={`shopping-remove-${it.name}`}
                                                    title="Rimuovi"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                            {/* Riga 2: assegnazione fornitore (solo modalità auto) */}
                                            {mode === "auto" && (
                                                <div className="mt-2 pt-2 border-t border-brand-line/60 flex items-center gap-2 flex-wrap">
                                                    <span className="text-[10px] uppercase tracking-wider text-brand-muted font-semibold">
                                                        Fornitore:
                                                    </span>
                                                    <select
                                                        value={override}
                                                        onChange={(e) => updateDraft(it.name, "supplier_override", e.target.value)}
                                                        data-testid={`shopping-supplier-${it.name}`}
                                                        className="flex-1 min-w-0 max-w-xs px-2 py-1 rounded-lg border border-brand-line text-[12px] bg-white focus:outline-none focus:border-brand-gold"
                                                    >
                                                        <option value="auto">
                                                            {hasMatch
                                                                ? `Auto — miglior prezzo (${bestCandidate.supplier_name} ${bestCandidate.price.toFixed(2)}€)`
                                                                : "Auto — Lista senza fornitore"}
                                                        </option>
                                                        <option value="generic">📋 Lista senza fornitore</option>
                                                        {suppliers.length > 0 && (
                                                            <optgroup label="Assegna manualmente a">
                                                                {suppliers.map((s) => {
                                                                    const c = candidates.find((c) => c.supplier_id === s.id);
                                                                    return (
                                                                        <option key={s.id} value={s.id}>
                                                                            {c
                                                                                ? `${s.name} (listino: ${c.price.toFixed(2)}€/${c.unit})`
                                                                                : `${s.name} (no listino)`}
                                                                        </option>
                                                                    );
                                                                })}
                                                            </optgroup>
                                                        )}
                                                    </select>
                                                    {resolvedSup && override !== "auto" && (
                                                        <span className="text-[11px] text-brand-success font-semibold">
                                                            → {resolvedSup.supplier_name}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="border-t border-brand-line pt-5">
                                <div className="eyebrow text-brand-ink/60 mb-3">Modalità ordine</div>
                                <div className="grid grid-cols-2 gap-2 mb-4">
                                    <button
                                        type="button"
                                        onClick={() => setMode("auto")}
                                        data-testid="mode-auto"
                                        className={`relative px-3 py-3 rounded-xl border text-left transition ${
                                            mode === "auto"
                                                ? "bg-brand-gold/15 border-brand-gold text-brand-navy"
                                                : "bg-white border-brand-line hover:border-brand-navy/50"
                                        }`}
                                    >
                                        <div className="flex items-center gap-1.5 font-bold text-sm">
                                            <Sparkles size={14} /> Automatica
                                            {mode === "auto" && <span className="ml-auto text-[9px] uppercase tracking-wider bg-brand-gold text-brand-navy px-1.5 py-0.5 rounded-full font-bold">Consigliata</span>}
                                        </div>
                                        <div className="text-[11px] text-brand-muted mt-1 leading-snug">
                                            Spacchetta in ordini separati per fornitore al miglior prezzo. Chi non è in listino finisce in "Ordine generico".
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setMode("manual")}
                                        data-testid="mode-manual"
                                        className={`px-3 py-3 rounded-xl border text-left transition ${
                                            mode === "manual"
                                                ? "bg-brand-navy text-white border-brand-navy"
                                                : "bg-white border-brand-line hover:border-brand-navy/50"
                                        }`}
                                    >
                                        <div className="flex items-center gap-1.5 font-bold text-sm">
                                            <Truck size={14} /> Singolo fornitore
                                        </div>
                                        <div className={`text-[11px] mt-1 leading-snug ${mode === "manual" ? "text-white/70" : "text-brand-muted"}`}>
                                            Tutti gli articoli vanno allo stesso fornitore (esistente o nuovo).
                                        </div>
                                    </button>
                                </div>

                                {mode === "manual" && (
                                    <>
                                        <div className="grid grid-cols-2 gap-2 mb-3">
                                            <button
                                                type="button"
                                                onClick={() => setSupplierMode("existing")}
                                                disabled={suppliers.length === 0}
                                                data-testid="supplier-mode-existing"
                                                className={`px-3 py-2 rounded-lg border text-sm font-semibold transition ${
                                                    supplierMode === "existing"
                                                        ? "bg-brand-navy text-white border-brand-navy"
                                                        : "bg-white text-brand-ink border-brand-line hover:border-brand-navy"
                                                } disabled:opacity-40 disabled:cursor-not-allowed`}
                                            >
                                                <Truck size={14} className="inline mr-1.5" /> Esistente
                                                {suppliers.length > 0 && (
                                                    <span className="ml-1 text-[10px] opacity-80">({suppliers.length})</span>
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setSupplierMode("new")}
                                                data-testid="supplier-mode-new"
                                                className={`px-3 py-2 rounded-lg border text-sm font-semibold transition ${
                                                    supplierMode === "new"
                                                        ? "bg-brand-navy text-white border-brand-navy"
                                                        : "bg-white text-brand-ink border-brand-line hover:border-brand-navy"
                                                }`}
                                            >
                                                <Plus size={14} className="inline mr-1.5" /> Nuovo fornitore
                                            </button>
                                        </div>

                                        {supplierMode === "existing" && suppliers.length > 0 && (
                                            <select
                                                value={supplierId}
                                                onChange={(e) => setSupplierId(e.target.value)}
                                                data-testid="supplier-select"
                                                className="w-full px-3 py-2 rounded-lg border border-brand-line bg-white text-sm focus:outline-none focus:border-brand-gold"
                                            >
                                                {suppliers.map((s) => (
                                                    <option key={s.id} value={s.id}>{s.name}</option>
                                                ))}
                                            </select>
                                        )}

                                        {supplierMode === "new" && (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                <input
                                                    placeholder="Nome fornitore *"
                                                    value={newSup.name}
                                                    onChange={(e) => setNewSup({ ...newSup, name: e.target.value })}
                                                    data-testid="new-supplier-name"
                                                    className="sm:col-span-2 px-3 py-2 rounded-lg border border-brand-line bg-white text-sm focus:outline-none focus:border-brand-gold"
                                                />
                                                <input
                                                    placeholder="Telefono"
                                                    value={newSup.contact}
                                                    onChange={(e) => setNewSup({ ...newSup, contact: e.target.value })}
                                                    data-testid="new-supplier-phone"
                                                    className="px-3 py-2 rounded-lg border border-brand-line bg-white text-sm focus:outline-none focus:border-brand-gold"
                                                />
                                                <input
                                                    placeholder="Email"
                                                    type="email"
                                                    value={newSup.email}
                                                    onChange={(e) => setNewSup({ ...newSup, email: e.target.value })}
                                                    data-testid="new-supplier-email"
                                                    className="px-3 py-2 rounded-lg border border-brand-line bg-white text-sm focus:outline-none focus:border-brand-gold"
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {list.length > 0 && (
                    <div className="border-t border-brand-line p-4 flex items-center justify-between gap-2 bg-paper">
                        <div className="text-[12px] text-brand-muted">
                            {list.length} articol{list.length === 1 ? "o" : "i"} ·{" "}
                            <span className="font-semibold text-brand-ink">
                                Totale {(() => {
                                    let t = 0;
                                    for (const it of list) {
                                        const d = draft[it.name];
                                        if (!d) continue;
                                        t += (parseFloat(d.quantity) || 0) * (parseFloat(d.unit_price) || 0);
                                    }
                                    return t.toFixed(2);
                                })()}€
                            </span>
                        </div>
                        <button
                            onClick={mode === "auto" ? createAutoSplitOrder : createManualOrder}
                            disabled={!canCreateOrder || busy}
                            data-testid="create-order-btn"
                            className="inline-flex items-center gap-2 rounded-full bg-brand-navy text-white px-5 py-2 text-sm font-bold hover:bg-brand-navyLight disabled:opacity-50 disabled:cursor-not-allowed transition"
                        >
                            {mode === "auto" ? <Sparkles size={14} /> : <FileText size={14} />}
                            {busy ? "Creazione…" : (mode === "auto" ? "Genera ordini automatici" : "Crea ordine")}
                        </button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
